import PropTypes from 'prop-types';
import moment from 'moment';
import { Box, Typography, Chip, IconButton, Tooltip, Stack, CircularProgress } from '@mui/material';
import useCustomSnackbar from 'src/hooks/useCustomSnackbar';
import { callApi } from 'src/utils/apiCall';
import useReferralFiles from 'src/hooks/useReferralFiles';
import Iconify from './Iconify';

// ─── Category labels ────────────────────────────────────────────────────────
export const REFERRAL_FILE_CATEGORY_LABELS = {
    REFERRAL_LETTER: 'Referral letter',
    XRAY: 'X-ray',
    SCAN_CBCT: 'CBCT scan',
    SCAN_INTRAORAL: 'Intraoral scan',
    CLINICAL_PHOTO: 'Clinical photo',
    OTHER: 'Other',
};

// ─── Status chip config ─────────────────────────────────────────────────────
export const REFERRAL_FILE_STATUS_CONFIG = {
    CLEAN: { label: 'Scanned – clean', color: 'success' },
    UNSCANNED: { label: 'Not virus-scanned', color: 'warning' },
    UPLOADED: { label: 'Checking…', color: 'info' },
    SCANNING: { label: 'Checking…', color: 'info' },
    PENDING_UPLOAD: { label: 'Uploading…', color: 'default' },
    INFECTED: { label: 'Blocked – malware', color: 'error' },
    REJECTED: { label: 'Rejected', color: 'error' },
};

export const formatFileSize = (bytes) => {
    const n = Number(bytes);
    if (!Number.isFinite(n) || n < 0) return '—';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
    return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const formatReceivedDate = (dateStr) => {
    if (!dateStr) return '—';
    const m = moment(dateStr);
    return m.isValid() ? m.format('DD MMM YYYY, h:mm A') : '—';
};

function ReferralFileRow({ file, practiceId, leadId, onError }) {
    const { enqueueSnackbar } = useCustomSnackbar();
    const statusCfg = REFERRAL_FILE_STATUS_CONFIG[file?.status] || { label: file?.status || 'Unknown', color: 'default' };
    const statusLabel = file?.status_label || statusCfg.label;
    const categoryLabel = REFERRAL_FILE_CATEGORY_LABELS[file?.category] || file?.category || 'Other';

    const handleDownload = async () => {
        try {
            const res = await callApi(
                `/api/v1/practices/${practiceId}/leads/${leadId}/referral-files/${file.id}/download/`,
                'GET'
            );
            const url = res?.data?.url;
            if (!(res?.ok || res?.status === 200) || !url) {
                throw new Error(res?.error || 'Unable to download file');
            }
            // Never cache the signed url — use it once, immediately.
            const a = document.createElement('a');
            a.href = url;
            a.target = '_blank';
            a.rel = 'noopener noreferrer';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        } catch (e) {
            const message = e?.message || 'Unable to download file. Please try again.';
            if (onError) onError(message);
            enqueueSnackbar(message, { variant: 'error' });
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 1.5,
                borderRadius: '10px',
                border: '1px solid rgba(145, 158, 171, 0.16)',
                bgcolor: 'common.white',
                transition: 'box-shadow 0.2s',
                '&:hover': { boxShadow: '0 2px 8px rgba(145, 158, 171, 0.16)' },
            }}
        >
            <Box
                sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '8px',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(7,141,238,0.08)',
                }}
            >
                <Iconify icon="solar:file-text-bold-duotone" width={20} sx={{ color: '#078DEE' }} />
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.25 }}>
                    <Typography
                        variant="body2"
                        sx={{
                            fontWeight: 600,
                            fontSize: '13px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                        title={file?.original_name}
                    >
                        {file?.original_name || 'Untitled file'}
                    </Typography>
                    <Chip
                        label={categoryLabel}
                        size="small"
                        sx={{ fontSize: '10px', height: 18, px: 0.5 }}
                    />
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                        Received {formatReceivedDate(file?.created_at)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                        {formatFileSize(file?.size)}
                    </Typography>
                </Stack>
            </Box>

            <Chip label={statusLabel} size="small" color={statusCfg.color} sx={{ fontSize: '11px', fontWeight: 600 }} />

            {file?.download_available && (
                <Tooltip title="Download file">
                    <IconButton size="small" onClick={handleDownload} sx={{ color: 'text.secondary' }}>
                        <Iconify icon="eva:download-fill" width={16} />
                    </IconButton>
                </Tooltip>
            )}
        </Box>
    );
}

ReferralFileRow.propTypes = {
    file: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        original_name: PropTypes.string,
        size: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        category: PropTypes.string,
        status: PropTypes.string,
        status_label: PropTypes.string,
        created_at: PropTypes.string,
        download_available: PropTypes.bool,
    }).isRequired,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    onError: PropTypes.func,
};

ReferralFileRow.defaultProps = {
    onError: null,
};

/**
 * "Referral files" panel — files a patient attached when submitting a
 * website referral (Gravity Forms + the referral-upload-key widget).
 *
 * Renders nothing when: the endpoint 404s (not deployed yet), or it returns
 * 200 with an empty list — this keeps the Communication tab strip free of
 * a dead entry until there is actually something to show.
 */
export default function ReferralFiles({ practiceId, leadId, state }) {
    const internal = useReferralFiles(state ? null : practiceId, state ? null : leadId);
    const { files, loading, notFound, error } = state || internal;

    if (notFound) return null;
    if (!loading && files.length === 0 && !error) return null;

    if (loading && files.length === 0) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    if (error && files.length === 0) {
        return (
            <Box sx={{ py: 3, textAlign: 'center' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Unable to load referral files right now.
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {files.map((file) => (
                <ReferralFileRow key={file.id} file={file} practiceId={practiceId} leadId={leadId} />
            ))}
        </Box>
    );
}

ReferralFiles.propTypes = {
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    // Optional controlled state (from a hook call made by the parent, e.g. to
    // also decide whether to show the tab that renders this component).
    // When provided, this component skips its own fetch.
    state: PropTypes.shape({
        files: PropTypes.array,
        loading: PropTypes.bool,
        notFound: PropTypes.bool,
        error: PropTypes.string,
    }),
};

ReferralFiles.defaultProps = {
    practiceId: null,
    leadId: null,
    state: null,
};
